/**
 * LeadsListaTerminal — lista de leads em estado TERMINAL fora do board ativo.
 *
 * Componente unico parametrizado por `tipo` que cobre as 3 abas auxiliares do
 * CRM (mesmo design da lista de Clientes — breadcrumb + busca + tabela `.dtable`):
 *
 *   - "arquivados": leads arquivados (?arquivado=true) — lead perdido volta por
 *                   "Reativar"; lead so arquivado, por "Desarquivar".
 *   - "perdidos":   leads perdidos  (?perdido=true)   — mostra motivo + reativa.
 *   - "ganhos":     leads ganhos    (?ganho=true)      — somente leitura.
 *
 * Cada linha abre o cockpit do lead. A acao de retorno de cada linha vem de
 * `acaoDeRetorno` (CRM-08): endpoint, rotulo, aviso e permissao dependem do
 * estado do lead, nao so da aba. DRY: os 3 wrappers
 * (LeadsArquivados/Perdidos/Ganhos) so passam `tipo`.
 *
 * CRM-45: a busca espera 350 ms e descarta resposta atrasada de uma busca
 * antiga (a de "s" chegava por ultimo e sobrescrevia a de "silva"); o
 * periodo `de`/`ate` recorta pela data do desfecho da aba (o backend tambem
 * ordena por ela); ExportMenu com as colunas visiveis + motivo da perda.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiArchive,
  FiAward,
  FiRotateCcw,
  FiSearch,
  FiXCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import AsyncButton from "../../components/AsyncButton";
import ExportMenu from "../../components/ExportMenu";
import { numeroParaMoeda } from "../../utils/leadFormat";
import { formatarDataLocal } from "../../utils/dinheiro";
import { salvarCSV, salvarExcel, salvarPDF } from "../../utils/exportacao";
import { useAuth } from "../../context/AuthContext";
import {
  STATUS_LEAD_LABEL,
  STATUS_LEAD_TONE,
  listarLeads,
} from "../../services/crm";
import { fmtData } from "../Orcamentos/helpers";
import { PageContainer } from "../../styles/shared";

import { montarExportacaoTerminal, nomeDoLead } from "./leadsTerminaisExportacao";
import { acaoDeRetorno } from "./retornoLeadTerminal";

// Mesmo intervalo do CrmBoard: 1 request por pausa na digitacao, nao por tecla.
const DEBOUNCE_BUSCA_MS = 350;

const SAVERS = { pdf: salvarPDF, excel: salvarExcel, csv: salvarCSV };

/** Rotulo do periodo escolhido, em data local (ADR-0059). */
function rotuloPeriodo({ de, ate }) {
  if (de && ate) return `${formatarDataLocal(de)} a ${formatarDataLocal(ate)}`;
  if (de) return `a partir de ${formatarDataLocal(de)}`;
  if (ate) return `até ${formatarDataLocal(ate)}`;
  return "";
}

// Configuracao por tipo de aba. `comRetorno` = a aba tem coluna de acao.
const CONFIG = {
  arquivados: {
    crumb: "Arquivados",
    titulo: "Leads arquivados",
    filtro: { arquivado: "true" },
    dataLabel: "Arquivado em",
    dataCampo: "arquivado_em",
    vazio: "Nenhum lead arquivado",
    iconeVazio: FiArchive,
    singular: "lead arquivado",
    plural: "leads arquivados",
    mostraMotivo: false,
    comRetorno: true,
  },
  perdidos: {
    crumb: "Perdidos",
    titulo: "Leads perdidos",
    filtro: { perdido: "true" },
    dataLabel: "Perdido em",
    dataCampo: "perdido_em",
    vazio: "Nenhum lead perdido",
    iconeVazio: FiXCircle,
    singular: "lead perdido",
    plural: "leads perdidos",
    mostraMotivo: true,
    comRetorno: true,
  },
  ganhos: {
    crumb: "Ganhos",
    titulo: "Leads ganhos",
    filtro: { ganho: "true" },
    dataLabel: "Ganho em",
    dataCampo: "ganho_em",
    vazio: "Nenhum lead ganho",
    iconeVazio: FiAward,
    singular: "lead ganho",
    plural: "leads ganhos",
    mostraMotivo: false,
    comRetorno: false,
  },
};

function avatarGradiente(nome) {
  let h = 0;
  for (const ch of nome || "x") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const a = (h % 8) + 1;
  let b = ((h >>> 3) % 8) + 1;
  if (b === a) b = (b % 8) + 1;
  return `linear-gradient(135deg, var(--chart-${a}), var(--chart-${b}))`;
}

function iniciais(nome) {
  const partes = String(nome || "?").trim().split(/\s+/);
  const a = partes[0]?.[0] || "?";
  const b = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (a + b).toUpperCase();
}

function StatusBadge({ status }) {
  if (!status) return <span className="badge neutral no-dot">—</span>;
  const tone = STATUS_LEAD_TONE[status] || "neutral";
  return (
    <span className={`badge ${tone} no-dot`} style={{ fontSize: 11 }}>
      {STATUS_LEAD_LABEL[status] || status}
    </span>
  );
}

function LeadsListaTerminal({ tipo }) {
  const cfg = CONFIG[tipo] || CONFIG.arquivados;
  const tipoEfetivo = CONFIG[tipo] ? tipo : "arquivados";
  const navigate = useNavigate();
  const { request, hasAllPermissions, isMaster } = useAuth();

  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [periodo, setPeriodo] = useState({ de: "", ate: "" });
  const [estado, setEstado] = useState({ loading: true, error: null, leads: [] });
  // Numero da ultima consulta disparada: resposta de consulta anterior (que
  // chegou atrasada) nao pode sobrescrever a lista da consulta atual.
  const consultaRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaAplicada(busca.trim()), DEBOUNCE_BUSCA_MS);
    return () => clearTimeout(timer);
  }, [busca]);

  const carregar = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    abortRef.current = controller;
    const consulta = consultaRef.current + 1;
    consultaRef.current = consulta;
    setEstado((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const payload = await listarLeads(
        request,
        {
          ...cfg.filtro,
          busca: buscaAplicada || undefined,
          de: periodo.de || undefined,
          ate: periodo.ate || undefined,
        },
        controller ? { signal: controller.signal } : {},
      );
      if (consulta !== consultaRef.current) return;
      const items = payload?.results || payload || [];
      setEstado({
        loading: false,
        error: null,
        leads: Array.isArray(items) ? items : [],
      });
    } catch (err) {
      if (consulta !== consultaRef.current || err?.name === "AbortError") return;
      setEstado({
        loading: false,
        error: err?.message || `Erro ao carregar os ${cfg.plural}.`,
        leads: [],
      });
    }
  }, [request, buscaAplicada, periodo, cfg.filtro, cfg.plural]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const periodoRotulo = rotuloPeriodo(periodo);
  const textoVazio = periodoRotulo ? `${cfg.vazio} no período.` : `${cfg.vazio}.`;

  const exportar = (formato) => {
    const saver = SAVERS[formato];
    if (!saver) return;
    saver(montarExportacaoTerminal({ cfg, leads: estado.leads, periodo: periodoRotulo }));
  };

  const subHeader = useMemo(() => {
    if (estado.loading) return "Carregando…";
    const n = estado.leads.length;
    if (n === 0) return `Nenhum ${cfg.singular}`;
    return `${n} ${n === 1 ? cfg.singular : cfg.plural}`;
  }, [estado.loading, estado.leads.length, cfg.singular, cfg.plural]);

  const podeExecutar = (acao) =>
    Boolean(acao) && (isMaster || hasAllPermissions(acao.permissao));

  const devolverAoPipeline = async (event, lead, acao) => {
    event.stopPropagation(); // nao abre o cockpit ao clicar no botao
    await acao.fn(request, lead.id);
    await carregar(); // sai da lista terminal — recarrega
  };

  const IconeVazio = cfg.iconeVazio;
  // Colunas: Lead, Status, Responsavel, [Motivo?], Data, Valor, [Acoes?].
  const totalCols =
    5 + (cfg.mostraMotivo ? 1 : 0) + (cfg.comRetorno ? 1 : 0);

  return (
    <PageContainer>
      {/* Cabecalho */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 18,
        }}
      >
        <div>
          <div className="breadcrumb">
            <span className="crumb">Início</span>
            <span className="crumb-sep">/</span>
            <span className="crumb">Comercial</span>
            <span className="crumb-sep">/</span>
            <span className="crumb">CRM</span>
            <span className="crumb-sep">/</span>
            <span className="crumb active">{cfg.crumb}</span>
          </div>
          <h1 className="page-title">{cfg.titulo}</h1>
          <div className="page-sub">{subHeader}</div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <ExportMenu onExport={exportar} disabled={estado.loading || estado.leads.length === 0} />
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => navigate("/crm")}
          >
            Voltar ao CRM
          </button>
        </div>
      </div>

      {/* Barra de busca */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <div className="input-prefixed" style={{ height: 32, minWidth: 320 }}>
          <FiSearch aria-hidden="true" />
          <input
            placeholder="Buscar lead, contato, apelido…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            aria-label={`Buscar ${cfg.singular}`}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          {cfg.dataLabel}: de
          <input
            type="date"
            className="input"
            style={{ height: 32 }}
            value={periodo.de}
            onChange={(e) => setPeriodo((p) => ({ ...p, de: e.target.value }))}
            aria-label={`${cfg.dataLabel} — de`}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          até
          <input
            type="date"
            className="input"
            style={{ height: 32 }}
            value={periodo.ate}
            onChange={(e) => setPeriodo((p) => ({ ...p, ate: e.target.value }))}
            aria-label={`${cfg.dataLabel} — até`}
          />
        </label>
        {periodoRotulo && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setPeriodo({ de: "", ate: "" })}
          >
            <FiXCircle aria-hidden="true" /> {periodoRotulo}
          </button>
        )}
      </div>

      {estado.error && (
        <div className="so-banner danger" role="alert" style={{ marginBottom: 12 }}>
          <FiAlertCircle aria-hidden="true" /> {estado.error}
        </div>
      )}

      {/* Tabela */}
      <div className="table-wrap">
        <table className="dtable">
          <colgroup>
            <col style={{ width: "28%" }} />
            <col style={{ width: "110px" }} />
            <col />
            {cfg.mostraMotivo && <col style={{ width: "22%" }} />}
            <col style={{ width: "140px" }} />
            <col style={{ width: "130px" }} />
            {cfg.comRetorno && <col style={{ width: "140px" }} />}
          </colgroup>
          <thead>
            <tr>
              <th>Lead</th>
              <th>Status</th>
              <th>Responsável</th>
              {cfg.mostraMotivo && <th>Motivo</th>}
              <th>{cfg.dataLabel}</th>
              <th className="num" style={{ textAlign: "right" }}>
                Valor
              </th>
              {cfg.comRetorno && <th aria-label="Ações" />}
            </tr>
          </thead>
          <tbody>
            {estado.loading ? (
              <tr>
                <td colSpan={totalCols} style={{ textAlign: "center", color: "var(--ink-3)", padding: 24 }}>
                  Carregando…
                </td>
              </tr>
            ) : estado.leads.length === 0 ? (
              <tr>
                <td colSpan={totalCols} style={{ textAlign: "center", color: "var(--ink-3)", padding: 24 }}>
                  <IconeVazio aria-hidden="true" style={{ verticalAlign: "middle", marginRight: 6 }} />
                  {textoVazio}
                </td>
              </tr>
            ) : (
              estado.leads.map((lead) => {
                const nome = nomeDoLead(lead);
                const acao = acaoDeRetorno(tipoEfetivo, lead);
                return (
                  <tr
                    key={lead.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/crm/leads/${lead.id}`)}
                    title={nome}
                  >
                    <td>
                      <div className="entity-row">
                        <div
                          className="avatar-sm"
                          style={{ background: avatarGradiente(nome), color: "white" }}
                        >
                          {iniciais(nome)}
                        </div>
                        <div className="entity-meta">
                          <div className="name">{nome}</div>
                          <div className="sub">{lead.titulo || lead.numero}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ overflow: "visible" }}>
                      <StatusBadge status={lead.status} />
                    </td>
                    <td>{lead.responsavel_nome || "—"}</td>
                    {cfg.mostraMotivo && (
                      <td title={lead.motivo_perda_detalhe || ""}>
                        {lead.motivo_perda_display || "—"}
                      </td>
                    )}
                    {/* Lead arquivado antes do carimbo `arquivado_em` existir cai na criacao. */}
                    <td>{fmtData(lead[cfg.dataCampo] || lead.created_at)}</td>
                    <td className={`num${lead.valor_estimado ? "" : " muted"}`}>
                      {lead.valor_estimado ? numeroParaMoeda(lead.valor_estimado) : "—"}
                    </td>
                    {cfg.comRetorno && (
                      <td style={{ textAlign: "right", overflow: "visible" }}>
                        {podeExecutar(acao) && (
                          <AsyncButton
                            variant="secondary"
                            onClick={(e) => devolverAoPipeline(e, lead, acao)}
                            successMessage={acao.sucesso}
                            // O `request` ja mostra o erro do backend em toast.
                            silentError
                            style={{ padding: "4px 10px", fontSize: "0.8rem" }}
                          >
                            <FiRotateCcw aria-hidden="true" /> {acao.label}
                          </AsyncButton>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </PageContainer>
  );
}

export default LeadsListaTerminal;
